import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Icon } from '~/components';

import { getLabelHierarchy } from '../../api/labels';
import {
  createTrackRollupIgnores,
  deleteTrackRollupIgnores,
  getTrackRollupIgnores,
} from '../../api/rollup-actions';
import type { TrackRollupRowKeyWithPrefix } from '../../api/track-rollup';
import { exportCSV, getPageData } from '../../api/track-rollup';
import { MenuItem } from '../../components/DataGrid/components/MenuItem';
import type {
  ColDef,
  DataGridState,
  DataLoader,
  DataQuery,
  ExportCSVPayload,
  ExportCSVResponse,
} from '../../components/DataGrid/types';
import { ViewType } from '../../components/DataGrid/types/views';
import {
  arrayUnique,
  camelToSnake,
  serialize,
} from '../../components/DataGrid/utils';
import { useAmplifyUser } from '../../context/useAmplifyUser';
import type { TrackRollupRow } from '../../types';
import { columnDefs } from './columns';
import { filterColumns } from './filter';

const defaultColDef: ColDef = {
  sortable: false,
  resizable: true,
};

const LazyTrackRollupGrid = lazy(() =>
  import('../../components/DataGrid').then((module) => ({
    default: module.DataGrid,
  })),
);

const searchFilter = (search: string): string => {
  if (search.startsWith('"') && search.endsWith('"')) {
    const s = search.slice(1, -1);

    return (
      `clean_title = '${s}' or ` +
      `artist = '${s}' or ` +
      `array_contains('${s}'::VARIANT, gpid_list) or ` +
      `array_contains('${s.toUpperCase()}'::VARIANT, isrc_list)`
    );
  }

  return (
    `clean_title ilike '${search}%' or ` +
    `artist ilike '${search}%' or ` +
    `concat('|', array_to_string(gpid_list, '|')) ilike '%|${search}%' or ` +
    `concat('|', array_to_string(isrc_list, '|')) ilike '%|${search}%'`
  );
};

const combineFilters = (
  search: string | undefined,
  filter: string | undefined,
): string => {
  const searchCriteria = search ? searchFilter(search) : '';
  const serializedFilter = filter || '';

  if (searchCriteria && serializedFilter) {
    return `${serializedFilter} AND (${searchCriteria})`;
  }

  if (searchCriteria) {
    return searchCriteria;
  }

  return serializedFilter.trim();
};

export function TrackRollup() {
  const { t } = useTranslation('label-perf');
  const navigate = useNavigate();
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [bulkMode, setBulkMode] = useState<boolean>(true);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<Partial<TrackRollupRow>[]>();
  const { user } = useAmplifyUser();
  const [enrichmentRefresher, setEnrichmentRefresher] = useState<string>('');

  const { data: labelHierarchyData, isLoading: isLoadingLabelsHierarchy } =
    useQuery({
      queryKey: ['labels-hierarchies-label-perf'],
      queryFn: () => getLabelHierarchy(),
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    });

  const filteredLabelHierarchyItems = useMemo(
    () => labelHierarchyData || [],
    [labelHierarchyData],
  );

  const ContextToolbar = useMemo(() => {
    // No Context Toolbar with no selected rows
    if (!selectedRows || selectedRows?.length === 0) {
      return undefined;
    }

    const canBeIgnored = selectedRows
      .filter((row) => !row.itemIgnore)
      .map((row) => row.hashKey!);

    const ignoredHashes = selectedRows
      .filter((row) => row.itemIgnore)
      .filter((row) => {
        if (user?.role === 'admin') {
          return true;
        } // Admin can unlock any track

        return row.itemIgnore!.self; // Only lock owner can unlock
      })
      .map((row) => row.hashKey!);

    const bulkExcludeHandler = async (hashes: string[]): Promise<void> => {
      setBulkLoading(true);
      await createTrackRollupIgnores(hashes);
      setBulkLoading(false);
      // Remove selection
      setBulkMode(false);
      setTimeout(() => setBulkMode(true), 0);
      // Refresh data
      setEnrichmentRefresher(new Date().toISOString());
    };

    const bulkIncludeHandler = async (hashes: string[]): Promise<void> => {
      setBulkLoading(true);
      await deleteTrackRollupIgnores(hashes);
      setBulkLoading(false);
      // Remove selection
      setBulkMode(false);
      setTimeout(() => setBulkMode(true), 0);
      // Refresh data
      setEnrichmentRefresher(new Date().toISOString());
    };

    return (
      <div className="flex items-center">
        {bulkLoading ? (
          <div className="flex items-center align-middle text-white">
            <Icon
              type="spinner"
              variant="solid"
              size="sm"
              className="fa-spin mr-sm"
            />{' '}
            {t('body.processing')}
          </div>
        ) : null}
        {!bulkLoading && (
          <>
            <p className="mr-sm text-white">{t('Bulk actions')}:</p>
            <div className="flex items-center gap-x-sm">
              <MenuItem
                counter={canBeIgnored.length}
                disabled={canBeIgnored.length === 0}
                icon="eye-slash"
                onClick={() => bulkExcludeHandler(canBeIgnored)}
                title="Ignore Gaps"
              />
              <MenuItem
                counter={ignoredHashes.length}
                disabled={ignoredHashes.length === 0}
                icon="eye"
                onClick={() => bulkIncludeHandler(ignoredHashes)}
                title="Process Gaps"
              />
            </div>
          </>
        )}
      </div>
    );
  }, [bulkLoading, user, selectedRows, t]);

  const downloadCSVFile = (url: string): void => {
    try {
      const downloadAnchor = document.createElement('a');

      downloadAnchor.style.display = 'none';
      downloadAnchor.href = url;
      downloadAnchor.download = 'CSV Export';
      downloadAnchor.click();
    } catch (err) {
      console.error('Error downloading CSV file:', err);
    }
  };

  const dataEnricher = useCallback(
    async (
      data: Partial<TrackRollupRow>[],
    ): Promise<Partial<TrackRollupRow>[]> => {
      try {
        if (data.length > 0 && data[0].hashKey) {
          const hashes = data.map((row) => row.hashKey!);
          const ignores = await getTrackRollupIgnores(hashes);

          data.forEach((row) => {
            const ignore = ignores.find((l) => l.hash_key === row.hashKey!);

            row.itemIgnore = ignore && {
              self: ignore.created_by.id === user?.id,
              owner: {
                name: ignore.created_by.name,
                email: ignore.created_by.email,
              },
              createdAt: new Date(ignore.created_at),
            };
          });
        }
      } catch (err) {
        console.error('Error enriching data:', (err as Error).message);
      }

      return Promise.resolve(data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, enrichmentRefresher], // enrichmentRefresher is needed to force re-enrichment
  );

  const exportCSVFile = async (context: DataGridState): Promise<void> => {
    const { select, order, filter } = context;
    const serializedFilter = serialize(filter);
    const combinedFilter = combineFilters(search, serializedFilter);

    const gridData: ExportCSVPayload = {
      select: arrayUnique(select.map((s) => camelToSnake(s))),
      order: order.map(
        (o) => `${o.direction === 'desc' ? '-' : ''}${camelToSnake(o.field)}`,
      ),
      filter: combinedFilter,
    };

    setExportLoading(true);

    const res = await exportCSV(gridData).catch((err) => {
      // eslint-disable-next-line no-console
      console.log('error', err);
      setExportLoading(false);
    });
    const url = (res as ExportCSVResponse)?.url;

    if (url) {
      downloadCSVFile(url);
      setExportLoading(false);
    }
  };

  const onExport = (context: DataGridState): void => {
    exportCSVFile(context).catch((err) => {
      // eslint-disable-next-line no-console
      console.log('error', err);
    });
  };

  const dataLoader: DataLoader<Partial<TrackRollupRow>> = useCallback(
    (query: DataQuery<Partial<TrackRollupRow>>) => {
      const combinedFilter = combineFilters(search, query.filter);

      return getPageData({
        ...query,
        offset: query.nextPageToken
          ? Number(query.nextPageToken.split('=')[1])
          : 0,
        select: query.select,
        order: query.order?.map((s) =>
          s.direction === 'asc' ? s.field : `-${s.field}`,
        ) as TrackRollupRowKeyWithPrefix[],
        filter: combinedFilter,
        wmg1_labels: query.wmg1_labels,
      });
    },
    [search],
  );

  return (
    <div className="flex h-[calc(100%-126px)] flex-wrap p-xl">
      <Helmet>
        <title>
          {t('title.catalog')} - {t('title.trackRollup')}
        </title>
      </Helmet>
      {user ? (
        <Suspense
          fallback={
            <div className="">
              <Icon
                data-testid="loading-spinner"
                type="spinner"
                variant="solid"
                size="sm"
                className="fa-spin mb-sm"
              />
            </div>
          }
        >
          <LazyTrackRollupGrid
            contextToolbarElement={ContextToolbar}
            onSelectionChanged={setSelectedRows}
            selectionMode={bulkMode ? 'multi' : undefined}
            dataEnricher={dataEnricher}
            columnDefs={columnDefs}
            dataLoader={dataLoader}
            defaultColDef={defaultColDef}
            enableConfig={true}
            enableExpand={true}
            enableExport={true}
            enableFilters={true}
            labelHierarchyData={filteredLabelHierarchyItems}
            labelsLoading={isLoadingLabelsHierarchy}
            exportLoading={exportLoading}
            filterColumns={filterColumns}
            id="track-rollup"
            limit={100}
            onExport={onExport}
            onSearch={setSearch}
            onViewChanged={(view) => {
              if (view === 'TrackRollup') {
                return;
              }

              if (view === 'Derivative') {
                navigate('../derivative');
              }

              if (view === 'Territories') {
                navigate('../territories');
              }
            }}
            viewIcon="microscope"
            views={[
              ViewType.Territories,
              ViewType.Derivative,
              ViewType.TrackRollup,
            ]}
          />
        </Suspense>
      ) : (
        <div className="block">
          <Icon
            type="spinner"
            variant="solid"
            size="sm"
            className="fa-spin mb-sm"
          />
        </div>
      )}
    </div>
  );
}

TrackRollup.displayName = 'TrackRollup';
